import { useState, type FormEvent } from "react";
import { categories } from "@/mocks/categories";

type FormResponse = {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
};

const SUBMIT_ADDRESS = "https://readdy.ai/api/form/dah7vdrp14a1h3mr6eng";

export default function LeadForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [detailLength, setDetailLength] = useState(0);

  const inputClass =
    "h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400";

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot.length > 0) {
      setStatus("success");
      form.reset();
      setDetailLength(0);
      return;
    }
    formData.delete("website_alt");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string") {
        payload.append(key, value);
      }
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDRESS, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const code = parsed?.code;
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
        setDetailLength(0);
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交未成功，请稍后重试或改用邮箱联系我们。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，线索未能提交，请检查网络后重试。");
    }
  }

  if (status === "success") {
    return (
      <div className="flex flex-col items-center rounded-lg border border-secondary-200 bg-secondary-50 px-6 py-14 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-500 text-background-50">
          <i className="ri-check-line text-3xl" />
        </span>
        <h2 className="mt-5 font-heading text-xl font-bold text-secondary-900">线索已收到，感谢你的信任</h2>
        <p className="mt-3 max-w-md text-sm leading-relaxed text-secondary-800">
          核实团队将在 48 小时内完成初步评估。若线索需要补充材料，我们会通过你预留的联系方式与你沟通。
          你也可以继续提交其他线索。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-6 whitespace-nowrap rounded-md bg-secondary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-700"
        >
          继续提交线索
        </button>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      id="lead-submit-form"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-7"
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label htmlFor="lead-title" className="mb-2 block text-sm font-medium text-foreground-900">
            线索标题 <span className="text-primary-600">*</span>
          </label>
          <input
            id="lead-title"
            name="title"
            type="text"
            required
            placeholder="用一句话概括线索内容，例如：某园区夜间排放异常"
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="lead-category" className="mb-2 block text-sm font-medium text-foreground-900">
            线索分类 <span className="text-primary-600">*</span>
          </label>
          <select id="lead-category" name="category" required defaultValue="" className={inputClass}>
            <option value="" disabled>
              请选择分类
            </option>
            {categories.map((category) => (
              <option key={category.id} value={category.name}>
                {category.name}
              </option>
            ))}
            <option value="其他">其他</option>
          </select>
        </div>

        <div>
          <label htmlFor="lead-region" className="mb-2 block text-sm font-medium text-foreground-900">
            涉及地区
          </label>
          <input
            id="lead-region"
            name="region"
            type="text"
            placeholder="例如：江苏 · 常州（可选）"
            className={inputClass}
          />
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="lead-detail" className="mb-2 flex items-center justify-between text-sm font-medium text-foreground-900">
            <span>
              线索详情 <span className="text-primary-600">*</span>
            </span>
            <span className={`text-xs ${detailLength > 500 ? "text-primary-600" : "text-foreground-500"}`}>
              {detailLength}/500
            </span>
          </label>
          <textarea
            id="lead-detail"
            name="detail"
            required
            maxLength={500}
            rows={6}
            onChange={(event) => setDetailLength(event.target.value.length)}
            placeholder="请描述时间、地点、涉及对象与关键信息，越具体越有助于核实（不超过 500 字）"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>

        <div>
          <label htmlFor="lead-nickname" className="mb-2 block text-sm font-medium text-foreground-900">
            署名 / 代号
          </label>
          <input
            id="lead-nickname"
            name="nickname"
            type="text"
            placeholder="可留代号，默认匿名处理（可选）"
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="lead-contact" className="mb-2 block text-sm font-medium text-foreground-900">
            联系方式
          </label>
          <input
            id="lead-contact"
            name="contact"
            type="text"
            placeholder="邮箱或手机号，便于对接（可选）"
            autoComplete="off"
            className={inputClass}
          />
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs leading-relaxed text-foreground-500">
          提交即表示你确认线索内容真实、合法。我们会按来源保护原则处理你的信息。
        </p>
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className="ri-send-plane-line" />
          {status === "submitting" ? "正在提交..." : "提交线索"}
        </button>
      </div>

      {status === "error" ? (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2.5 text-sm text-primary-700">
          <i className="ri-error-warning-line mt-0.5" />
          {formError}
        </p>
      ) : null}

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-aux-field"
      />
    </form>
  );
}